/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  IconButton,
  Image,
  useBreakpointValue,
  Drawer,
  Portal,
} from 'chakra-v3';
import { Text } from '../../components/Text';
import { Button } from '../../components/buttons/Index';
import { CiMenuBurger } from 'react-icons/ci';
import { MdLeaderboard, MdClose } from 'react-icons/md';
import methodologyIcon from '../../assets/fluent_flow-dot-20-filled.svg';
import { VscLightbulbSparkle } from "react-icons/vsc";


// Feature flag: Set to true only if the event website is live before the event
const SAMIKSHA_EVENT_LINK_LIVE = true;

interface NavLink {
  label: string;
  path: string;
  icon: React.ReactElement;
  external?: boolean;
}

const navLinks: NavLink[] = [
  {
    label: 'Key Takeaways',
    path: '#insights-and-recommendations',
    icon: <VscLightbulbSparkle size={24} color="currentColor" />,
  },
  {
    label: 'Leaderboard',
    path: '#leaderboard',
    icon: <MdLeaderboard size={24} color="currentColor" />,
  },
  {
    label: 'Methodology',
    path: '#methodology',
    icon: <Image src={methodologyIcon} alt="Methodology" w="24px" h="24px" />,
  },
];

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navLinks: NavLink[];
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, navLinks }) => {
  const handleLinkClick = (link: NavLink) => {
    if (link.external && typeof window !== 'undefined') {
      window.open(link.path, '_blank', 'noopener,noreferrer');
    } else if (link.path.includes('#')) {
      const hash = link.path.split('#')[1];
      const element = document.getElementById(hash);
      if (element) {
        // Calculate header height dynamically
        const header = document.querySelector('nav[data-sticky-header]') as HTMLElement;
        const headerHeight = header ? header.offsetHeight : 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
      onClose();
    } else {
      window.location.href = link.path;
      onClose();
    }
  };

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(e) => (!e.open ? onClose() : undefined)}
      placement="end"
      size={{ base: 'full', sm: 'xs' }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg="bg.primary" maxW="90vw" w={{ base: '100%', sm: '360px' }}>
            <Drawer.Header
              borderBottom="1px solid"
              borderColor="rgba(255, 255, 255, 0.1)"
              px={{ base: 4, sm: 6 }}
              py={4}
            >
              <Flex justify="space-between" align="center" w="100%">
                <Drawer.Title
                  fontWeight="600"
                  color="text.primary"
                  fontSize={{ base: '20px', sm: '22px' }}
                  fontFamily="heading"
                >
                  Menu
                </Drawer.Title>
                <Drawer.CloseTrigger asChild>
                  <IconButton
                    aria-label="Close menu"
                    variant="ghost"
                    size="sm"
                    _hover={{ opacity: 0.8 }}
                    color="text.primary"
                    minW="40px"
                    h="40px"
                  >
                    <MdClose size={24} color="white" />
                  </IconButton>
                </Drawer.CloseTrigger>
              </Flex>
            </Drawer.Header>
            <Drawer.Body px={{ base: 4, sm: 6 }} py={6}>
              <VStack gap={2} alignItems="stretch">
                {navLinks.map((link) => (
                  <Box
                    key={link.path}
                    as="button"
                    onClick={() => handleLinkClick(link)}
                    bg="transparent"
                    border="none"
                    p={0}
                    display="flex"
                    gap="16px"
                    alignItems="center"
                    px={{ base: 3, sm: 4 }}
                    py={{ base: 3, sm: 4 }}
                    color="text.primary"
                    cursor="pointer"
                    borderRadius="8px"
                    _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                    transition="background-color 0.2s"
                    w="100%"
                    textAlign="left"
                  >
                    <Box
                      w={{ base: '24px', sm: '28px' }}
                      h={{ base: '24px', sm: '28px' }}
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {link.icon}
                    </Box>
                    <Text
                      whiteSpace="nowrap"
                      fontSize={{ base: '16px', sm: '18px' }}
                      fontWeight="normal"
                      fontFamily="body"
                    >
                      {link.label}
                    </Text>
                  </Box>
                ))}
                {SAMIKSHA_EVENT_LINK_LIVE && (
                  <Box mt={4} pt={4} borderTop="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
                    <Button
                      variant="cta"
                      w="100%"
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          window.open('https://karya.in/contact', '_blank', 'noopener,noreferrer');
                        }
                        onClose();
                      }}
                    >
                      Contact Us
                    </Button>
                  </Box>
                )}
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

/**
 * SamikshaHeader component providing sticky, responsive navigation for Samiksha microsite.
 */
const SamikshaHeader: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;

  useEffect(() => {
    // Close mobile menu if resizing to desktop
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  const handleCloseMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleNavigate = useCallback((path: string) => {
    if (path.includes('#')) {
      const hash = path.split('#')[1];
      const element = document.getElementById(hash);
      if (element) {
        // Calculate header height dynamically
        const header = document.querySelector('nav[data-sticky-header]') as HTMLElement;
        const headerHeight = header ? header.offsetHeight : 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    } else {
      window.location.href = path;
    }
  }, []);

  const handleLinkClick = (link: NavLink) => {
    if (link.external && typeof window !== 'undefined') {
      window.open(link.path, '_blank', 'noopener,noreferrer');
    } else {
      handleNavigate(link.path);
    }
  };

  return (
    <>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={handleCloseMobileMenu} navLinks={navLinks} />
      <Box
        as="nav"
        bg="bg.primary"
        px={{ base: 4, sm: 5, md: 20 }}
        py={{ base: 4, md: 5 }}
        position="sticky"
        top={0}
        zIndex={1000}
        data-sticky-header
      >
        <Flex align="center" justify="space-between" w="100%" maxW="1440px" mx="auto">
          {/* Brand Name */}
          <Text
            fontFamily="heading"
            fontSize={{ base: '22px', sm: '26px', md: '32px', lg: '32px' }}
            fontWeight="normal"
            lineHeight={{ base: '32px', md: '48px', lg: '48px' }}
            letterSpacing="-1.5px"
            color="text.primary"
            whiteSpace="nowrap"
            cursor="pointer"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            _hover={{ opacity: 0.8 }}
            transition="opacity 0.2s"
            flexShrink={0}
          >
            Samiksha
          </Text>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Flex
              position="absolute"
              left="50%"
              transform="translateX(-50%)"
              justify="center"
              align="center"
              gap={0}
              display={{ base: 'none', lg: 'flex' }}
            >
              <HStack gap={0} align="center">
                {navLinks.map((link, index) => (
                  <React.Fragment key={link.path}>
                    <Box
                      as="button"
                      onClick={() => handleLinkClick(link)}
                      bg="transparent"
                      border="none"
                      p={0}
                      display="flex"
                      gap="12px"
                      alignItems="center"
                      px="12px"
                      py="8px"
                      color="text.primary"
                      cursor="pointer"
                      _hover={{ opacity: 0.8 }}
                      transition="opacity 0.2s"
                      borderRadius="4px"
                    >
                      <Box
                        w="24px"
                        h="24px"
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {link.icon}
                      </Box>
                      <Text whiteSpace="nowrap" fontSize="16px" fontFamily="body">
                        {link.label}
                      </Text>
                    </Box>
                    {index < navLinks.length - 1 && (
                      <Box w="0px" h="16px" flexShrink={0} position="relative" mx="20px">
                        <Box
                          position="absolute"
                          inset="0"
                          borderLeft="1px solid"
                          borderColor="text.primary"
                          opacity={0.3}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </HStack>
            </Flex>
          )}

          {/* CTA Button - Desktop */}
          {!isMobile && SAMIKSHA_EVENT_LINK_LIVE && (
            <Button
              variant="cta"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open('https://karya.in/contact', '_blank', 'noopener,noreferrer');
                }
              }}
              display={{ base: 'none', lg: 'flex' }}
            >
              Contact Us
            </Button>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              aria-label="Open menu"
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(true)}
              _hover={{ opacity: 0.8, bg: 'rgba(255, 255, 255, 0.1)' }}
              color="text.primary"
              display="flex"
              alignItems="center"
              justifyContent="center"
              minW={{ base: '36px', sm: '40px' }}
              h={{ base: '36px', sm: '40px' }}
              borderRadius="8px"
              transition="all 0.2s"
            >
              <CiMenuBurger size={24} color="white" />
            </IconButton>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default SamikshaHeader;
